import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CachedValuesService } from '@services/index';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FormGroup, Validators } from '@angular/forms';
import { AgreementForm, AgreementPeriodForm } from '../../shared-agreement.model';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';
import { Observable, map } from 'rxjs';
import { ListTypeItem } from 'app/types/list-type-item.type';
import {
    fromNgbDate,
    toDateISOStringFromDateTime,
    toNgbDate,
    toNgbDateFromDate,
    utcNow
} from '@helpers/datetime-helper';
import { DateTime } from 'luxon';
import { TranslateService } from '@ngx-translate/core';
import { AgreementFormHelper } from '../../shared-agreement.helper';
import { BaseHighlightComponent } from '@shared/components/base-highlight/base-highlight.component';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_ROUTES } from 'app/constants';
import { AgreementRoutes, AgreementsRouteId } from 'app/features/agreement/agreement.routes';
import { AgreementComponent } from 'app/features/agreement/pages/agreement/agreement.component';

@UntilDestroy()
@Component({
    selector: 'app-agreement-periods',
    templateUrl: './agreement-periods.component.html',
    styleUrl: './agreement-periods.component.scss'
})
export class AgreementPeriodsComponent extends BaseHighlightComponent implements OnInit, OnDestroy {
    @Input() public periodForm: FormGroup<AgreementPeriodForm>;
    @Input() public agreementForm: FormGroup<AgreementForm>;
    @Input() public showPotentialValidationMessages: boolean;
    private readonly cachedValuesService = inject(CachedValuesService);
    private readonly translateService = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly agreementComponent = inject(AgreementComponent, { optional: true, skipSelf: true });

    public readonly commitmentperiodtypes$: Observable<DropdownItem<number>[]> =
        this.cachedValuesService.commitmentperiodtypes$.pipe(
            map((data) =>
                data.map((item: ListTypeItem) => {
                    return {
                        value: parseInt(item.name),
                        label: this.translateService.instant(
                            'FEATURES.AGREEMENTS.COMMITMENT_PERIOD_TYPES.' + item.name
                        ) as string
                    } as DropdownItem<number>;
                })
            )
        );

    public periodTypes: DropdownItem<number>[] = [
        {
            label: this.translateService.instant('GENERAL.DAYS') as string,
            value: 0
        },
        { label: this.translateService.instant('GENERAL.MONTHS') as string, value: 1 }
    ];

    public get f() {
        return this.periodForm.controls;
    }

    public get isInventoryType() {
        return this.agreementForm.controls.details.value.agreementTypeId === 13; // Inventory
    }

    public override ngOnInit() {
        super.ngOnInit();
        this.f.commitmentPeriodTypeId.valueChanges.subscribe((typeId) => {
            this.validToValidator(typeId);
        });

        this.validToValidator();
    }

    public override ngOnDestroy() {
        super.ngOnDestroy();
    }

    private validToValidator(currentTypeId: number = this.f.commitmentPeriodTypeId.value) {
        if (currentTypeId == -1) {
            this.f.validTo.setValue(null);
            this.f.validTo.clearValidators();
        } else {
            this.f.validTo.setValidators(Validators.required);
        }
        this.f.validTo.updateValueAndValidity();
    }

    public showValidTo() {
        return this.periodForm.controls.commitmentPeriodTypeId.value >= 0;
    }

    public isNotInventory() {
        return this.agreementForm.controls.details.value.agreementTypeId !== 13 /* Inventory */;
    }

    public validToIsReadOnly() {
        return this.periodForm.controls.commitmentPeriodTypeId.value > 0;
    }

    public validFromChanged() {
        this.commitmentPeriodChanged(this.f.commitmentPeriodTypeId.value);
    }
    public commitmentPeriodChanged(commitPeriodTypeId: number) {
        if (commitPeriodTypeId > 0) {
            const fromDate = fromNgbDate(this.f.validFrom.value);
            const newDate = this.calculateDateFromCommitPeriod(commitPeriodTypeId, fromDate);
            this.f.validTo.setValue(toNgbDate(newDate));
        }
    }

    private calculateDateFromCommitPeriod(commitPeriodTypeId: number, fromDate: DateTime) {
        if (!this.f.validFrom.value) fromDate = utcNow();
        const newDate = fromDate.plus({ months: commitPeriodTypeId });
        return newDate;
    }

    public toggleNoticePeriodAfterExtension() {
        if (this.f.noticePeriodAfterExtension.value > 0) {
            this.f.noticePeriodAfterExtension.setValue(null);
        } else {
            this.f.noticePeriodAfterExtension.setValue(1);
            this.f.noticePeriodTypeIdAfterExtension.setValue(1);
        }
        this.periodForm.markAsDirty();
    }

    public toggleTerminated() {
        if (this.isTerminated) {
            this.f.terminationDate.setValue(null);
            this.f.expiryDateAfterTermination.setValue(null);
        } else {
            this.f.terminationDate.setValue(toNgbDate(utcNow()));
            const terminationDate = AgreementFormHelper.getTerminationDate(this.periodForm?.controls);
            if (terminationDate) {
                this.f.expiryDateAfterTermination.setValue(toNgbDateFromDate(terminationDate));
            }
            this.periodForm.controls.managedDate.setValue(null);
        }
        this.f.replacedByAgreementId.setValue(null);
        this.periodForm.markAsDirty();
    }

    public get isTerminated() {
        return !!this.f.terminationDate?.value && !this.f?.replacedByAgreementId?.value;
    }

    public get isTerminatedAndReplaced() {
        return !!this.f.terminationDate?.value && !!this.f?.replacedByAgreementId?.value;
    }

    public toggleTerminateAndReplace() {
        if (this.isTerminatedAndReplaced) {
            this.f.terminationDate.setValue(null);
            this.f.expiryDateAfterTermination.setValue(null);
            this.f.replacedByAgreementId.setValue(null);
        } else {
            this.f.terminationDate.setValue(toNgbDate(utcNow()));
            const terminationDate = AgreementFormHelper.getTerminationDate(this.periodForm?.controls);
            if (terminationDate) {
                this.f.expiryDateAfterTermination.setValue(toNgbDateFromDate(terminationDate));
            }
            this.f.replacedByAgreementId.setValue('-TEMP-');
            this.periodForm.controls.managedDate.setValue(null);
        }
        this.periodForm.markAsDirty();
    }

    public toggleManaged() {
        if (this.isManaged) {
            this.periodForm.controls.managedDate.setValue(null);
        } else {
            this.periodForm.controls.managedDate.setValue(toNgbDate(utcNow()));
            this.f.terminationDate.setValue(null);
            this.f.expiryDateAfterTermination.setValue(null);
        }
        this.periodForm.markAsDirty();
    }

    public get isManaged() {
        return !!this.periodForm?.controls.managedDate.value;
    }

    public get managed() {
        const managed = fromNgbDate(this.periodForm.controls.managedDate.value);
        return toDateISOStringFromDateTime(managed);
    }

    public get lastDateForNotice(): string {
        return AgreementFormHelper.getlastDateForNoticeToString(this.f);
    }

    public get noticeDate(): Date | null {
        return AgreementFormHelper.getNoticeDate(this.f);
    }

    public get validToAfterExtension(): Date | null {
        return AgreementFormHelper.getValidToAfterExtension(this.f);
    }

    public goToReplacedAgreement() {
        const replacedByAgreementId = this.f.replacedByAgreementId.value;
        if (replacedByAgreementId) {
            window.location.href = `/${APP_ROUTES.Agreement}/details/${replacedByAgreementId}`;
        }
    }

    public async goToCreateNewAgreement() {
        try {
            if (this.agreementComponent) {
                await this.agreementComponent.saveAndWait();
            }
            const agreementId = this.route.snapshot.paramMap.get(AgreementsRouteId.AgreementId);
            void this.router.navigate([`/${APP_ROUTES.Agreement}/${AgreementRoutes.CreateNewAgreement}`], {
                queryParams: {
                    agreementtypeid: 1,
                    replaceagreementid: agreementId
                }
            });
        } catch {
            // stay on page if save fails
        }
    }
}
